import { FC } from 'react';
import { ComboBox } from '@carbon/react';
import {
  ColumnDateFilter,
  FilterSection,
  useDataTableContext,
} from 'components/data-table';
import type {
  FormationManagementFacets,
  GeologicIntervalListQueryResponse,
} from 'types/api-responses';
import styles from './formation-management-filters.module.scss';

const ALL_BASINS = 'All Basins';
const ALL_INTERVALS = 'All Intervals';
const ALL_CREATORS = 'All Users';
const SYSTEM_CREATOR = 'System';

interface ColumnSearchableSelectProps {
  columnId: string;
  inputId: string;
  label: string;
  /** Placeholder shown when no value is selected (e.g. "All Basins"). */
  emptyPlaceholder: string;
  items: string[];
}

/**
 * Column-bound single-select filter rendered as a Carbon `ComboBox` so the
 * list is typeahead-searchable. Selecting an item writes a single-element
 * array to the column's filter state; clearing via the × button (or picking
 * nothing) drops the filter back to `undefined`.
 * @param {ColumnSearchableSelectProps} props - See interface.
 * @return {JSX.Element} The filter input.
 */
const ColumnSearchableSelect: FC<ColumnSearchableSelectProps> = ({
  columnId,
  inputId,
  label,
  emptyPlaceholder,
  items,
}) => {
  const { table } = useDataTableContext<GeologicIntervalListQueryResponse>();
  const column = table.getColumn(columnId);
  const filterValue = column?.getFilterValue() as string[] | undefined;
  const selectedItem =
    filterValue && filterValue.length === 1 ? filterValue[0]! : null;

  return (
    <div className={styles.detailsFilterField}>
      <label className={styles.detailsFilterLabel} htmlFor={inputId}>
        {label}
      </label>
      <ComboBox
        id={inputId}
        titleText=""
        placeholder={emptyPlaceholder}
        items={items}
        itemToString={(item) => item ?? ''}
        selectedItem={selectedItem}
        onChange={({ selectedItem: next }) => {
          column?.setFilterValue(next ? [next] : undefined);
        }}
      />
    </div>
  );
};

export interface FormationManagementFiltersProps {
  facets?: FormationManagementFacets;
}

export const FormationManagementFilters: FC<
  FormationManagementFiltersProps
> = ({ facets }) => {
  const facetCreators = facets?.creators ?? [];
  const creatorItems = facetCreators.includes(SYSTEM_CREATOR)
    ? facetCreators
    : [SYSTEM_CREATOR, ...facetCreators];

  return (
    <div className={styles.filtersWrapper}>
      <FilterSection title="Date Filter">
        <ColumnDateFilter columnId="createdAt" label="Created Date" />
        <ColumnDateFilter columnId="updatedAt" label="Updated Date" />
      </FilterSection>

      <FilterSection title="Formation Filters">
        <ColumnSearchableSelect
          columnId="basinName"
          inputId="fm-basin"
          label="Basin"
          emptyPlaceholder={ALL_BASINS}
          items={facets?.basins ?? []}
        />
        <ColumnSearchableSelect
          columnId="geologicInterval"
          inputId="fm-interval"
          label="Geologic Interval"
          emptyPlaceholder={ALL_INTERVALS}
          items={facets?.intervals ?? []}
        />
        <ColumnSearchableSelect
          columnId="createdBy"
          inputId="fm-creator"
          label="Created By"
          emptyPlaceholder={ALL_CREATORS}
          items={creatorItems}
        />
      </FilterSection>
    </div>
  );
};
